import { useMemo, useState } from "react";
import type { Sentiment, TranscriptRecord } from "../types";
import { Modal } from "react-bootstrap";
import { getSentimentColor, getSentimentChurnSeverityColor, getSentimentPositiveSeverityColor } from "../utils";
import { trellisPalette } from "components/custom/analytics/palettes";
import { FaCircle } from "react-icons/fa";
import { IoTime } from "react-icons/io5";
import { css } from "../../../../styled-system/css";

const SentimentBox = ({ transcriptData }: { transcriptData: TranscriptRecord[] }) => {
  const [showModal, setShowModal] = useState(false);

  const sentiment: Sentiment | undefined = useMemo(() => {
    const sentimentItems = transcriptData.filter((item) => item.type === "sentiment");

    if (sentimentItems.length === 0) {
      return undefined;
    }
    try {
      if (sentimentItems[0]?.content) {
        return JSON.parse(sentimentItems[0].content) as Sentiment;
      }
      return undefined;
    } catch {
      return undefined;
    }
  }, [transcriptData]);

  if (!sentiment) {
    return <div>No sentiment found.</div>;
  }

  // helper from earlier
  function splitByTimestamps(text: string): string[] {
    const regex = /(\d{2}:\d{2}:\d{2})/g;
    return text
      .split(regex)
      .map((p) => p.trim())
      .filter(Boolean);
  }

  type JustificationProps = {
    justification: string;
    file_id?: string;
  };

  const Justification = ({ justification, file_id }: JustificationProps) => {
    const parts = useMemo(() => splitByTimestamps(justification), [justification]);

    return (
      <span>
        {parts.map((part, index) => {
          const isTimestamp = /^\d{2}:\d{2}:\d{2}$/.test(part);

          if (isTimestamp) {
            const timeValue = part;
            return (
              <span
                key={index}
                style={{ color: trellisPalette[1], cursor: "pointer", marginRight: "5px" }}
                onClick={() => {
                  if (file_id) {
                    window.location.hash = `id=${file_id}&time=${timeValue}`;
                  }
                }}
              >
                <IoTime /> {timeValue}
              </span>
            );
          }

          return (
            <span key={index} className="fs-standard">
              {part}{" "}
            </span>
          );
        })}
      </span>
    );
  };

  return (
    <>
      <div className={css({ marginBottom: "1rem" })}>
        <div className={css({ display: "flex", marginBottom: "2rem" })}>
          <div className={css({ flex: 0.6, paddingRight: "1rem", textAlign: "left" })}>
            <span className={css({ fontSize: "xlarge", fontWeight: "bold" })}>Sentiment Analysis</span>
          </div>
          <div
            className={css({
              cursor: "pointer",
              flex: 0.4,
              borderRadius: "5px",
              paddingLeft: "2rem",
              paddingRight: "2rem",
              paddingTop: "0.5rem",
              textAlign: "center",
            })}
            style={{ backgroundColor: getSentimentColor(sentiment["Customer Mood"].Mood) }}
            onClick={() => setShowModal(true)}
          >
            <span className={css({ fontSize: "small" })}>({sentiment["Churn Risk Status"].Status})</span>
          </div>
        </div>

        <div className={css({ fontSize: "medium" })}>
          {transcriptData.length > 0 && (
            <Justification
              justification={sentiment["Churn Risk Status"].Justification}
              file_id={transcriptData[0]?.file_id}
            />
          )}
        </div>
      </div>
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <span className={css({ fontSize: "xlarge", fontWeight: "bold" })}>Sentiment Analysis Details</span>
        </Modal.Header>
        <Modal.Body>
          <div className={css({ margin: "1rem", fontSize: "medium" })}>
            <span className={css({ fontSize: "large", fontWeight: "bold" })}>Key Themes</span>
            <ul>
              {sentiment["Key Themes"].map((theme, index) => (
                <li key={index}>{theme}</li>
              ))}
            </ul>
            <span className={css({ fontSize: "large", fontWeight: "bold" })}>Churn Risk Indicators</span>
            <ul>
              {sentiment["Churn Risk Indicators"].map((indicator, index) => (
                <li key={index}>
                  <FaCircle color={getSentimentChurnSeverityColor(indicator.severity)} /> <em>{indicator.category}</em>{" "}
                  - {indicator.canonical_text}
                </li>
              ))}
            </ul>
            <span className={css({ fontSize: "large", fontWeight: "bold" })}>Positive Signals</span>
            <ul>
              {sentiment["Positive Signals"].map((signal, index) => (
                <li key={index}>
                  <FaCircle color={getSentimentPositiveSeverityColor(signal.severity)} /> <em>{signal.category}</em> -{" "}
                  {signal.canonical_text}
                </li>
              ))}
            </ul>
            <span className={css({ fontSize: "large", fontWeight: "bold" })}>Recommendations</span>
            <ul>
              {sentiment["Recommendations"].map((recommendation, index) => (
                <li key={index}>{recommendation}</li>
              ))}
            </ul>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SentimentBox;
